import type { CustomizationFlag } from "../model/customizationFlags";
import type { DrivingSchool } from "../providers/drivingSchoolsProvider";

export type BkfNavigationItem = {
  id: "overview" | "courses" | "scheduler" | "autoForms" | "courseTemplates" | "timetables" | "rooms" | "fleet";
  label: string;
  description: string;
  to: string;
  isActive: boolean;
  disabledReason?: string;
};

export function getBkfNavigationItems(props: {
  drivingSchoolId: string;
  drivingSchool: DrivingSchool | undefined;
  pathname: string;
  hasGrant: (grant: Grant) => boolean;
}): Array<BkfNavigationItem> {
  const { drivingSchoolId, drivingSchool, pathname, hasGrant } = props;
  const hasCustomization = (customization: CustomizationFlag) =>
    drivingSchool?.customizations.includes(customization) ?? false;
  const bkfFleetTab = hasCustomization("trucks") ? "trucks" : "buses";
  const getDisabledReason = (options: { featureEnabled: boolean; hasPermission?: boolean }) => {
    if (!options.featureEnabled) {
      return "Für Ihre Fahrschule nicht freigeschaltet";
    }
    if (options.hasPermission === false) {
      return "Keine Berechtigung";
    }
    return undefined;
  };

  return [
    {
      id: "overview",
      label: "Übersicht",
      description: "Alle Funktionen für die Berufskraftfahrer-Ausbildung auf einen Blick.",
      to: `/drivingSchools/${drivingSchoolId}/bkf`,
      isActive: pathname === `/drivingSchools/${drivingSchoolId}/bkf`,
    },
    {
      id: "courses",
      label: "BKF-Kurse",
      description: "BKF-Kurse planen, durchführen und verwalten.",
      to: `/drivingSchools/${drivingSchoolId}/courses`,
      isActive: false,
      disabledReason: getDisabledReason({ featureEnabled: hasCustomization("bkfCourses") }),
    },
    {
      id: "scheduler",
      label: "Terminplaner",
      description: "Termine für Fahrlehrer, Fahrzeuge und weitere Ressourcen koordinieren.",
      to: `/drivingSchools/${drivingSchoolId}/scheduler`,
      isActive: pathname === `/drivingSchools/${drivingSchoolId}/scheduler`,
      disabledReason: getDisabledReason({ featureEnabled: true, hasPermission: hasGrant("Scheduler:view") }),
    },
    {
      id: "autoForms",
      label: "Auto-Formulare",
      description: "Dokumente und Formulare aus Schülerdaten erstellen.",
      to: `/drivingSchools/${drivingSchoolId}/autoForms`,
      isActive: pathname === `/drivingSchools/${drivingSchoolId}/autoForms`,
      disabledReason: getDisabledReason({
        featureEnabled: true,
        hasPermission: hasGrant("DrivingSchool:AutoForm:list"),
      }),
    },
    {
      id: "courseTemplates",
      label: "Kursvorlagen",
      description: "BKF-Kurse mit Modulen, Unterrichtseinheiten und Farben vorbereiten.",
      to: `/drivingSchools/${drivingSchoolId}/courseTemplates`,
      isActive: pathname === `/drivingSchools/${drivingSchoolId}/courseTemplates`,
      disabledReason: getDisabledReason({
        featureEnabled: hasCustomization("bkfCourses"),
        hasPermission: hasGrant("CourseTemplate:list"),
      }),
    },
    {
      id: "timetables",
      label: "Stundenpläne",
      description: "Unterrichtszeiten für die Planung und automatische Terminbefüllung hinterlegen.",
      to: `/drivingSchools/${drivingSchoolId}/timetables`,
      isActive: pathname === `/drivingSchools/${drivingSchoolId}/timetables`,
      disabledReason: getDisabledReason({
        featureEnabled: hasCustomization("bkfCourses"),
        hasPermission: hasGrant("Timetable:list"),
      }),
    },
    {
      id: "rooms",
      label: "Räume",
      description: "Unterrichtsräume für die Durchführung von BKF-Kursen verwalten.",
      to: `/drivingSchools/${drivingSchoolId}/rooms`,
      isActive: pathname === `/drivingSchools/${drivingSchoolId}/rooms`,
      disabledReason: getDisabledReason({ featureEnabled: hasCustomization("bkfCourses") }),
    },
    {
      id: "fleet",
      label: "Fuhrpark",
      description: "LKWs und Busse für die Berufskraftfahrer-Ausbildung konfigurieren.",
      to: `/drivingSchools/${drivingSchoolId}/fleet#${bkfFleetTab}`,
      isActive: false,
      disabledReason: getDisabledReason({
        featureEnabled: hasCustomization("trucks") || hasCustomization("buses"),
        hasPermission: hasGrant("Fleet:list"),
      }),
    },
  ];
}
